/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * A page that outlives a deploy. Every build hashes its chunks and a deploy
 * removes the old files, so a tab opened before it fails on its next lazy
 * import: Chrome says "Failed to fetch dynamically imported module", Safari
 * "Importing a module script failed.", Vite's own preload of a stylesheet
 * "Unable to preload CSS for …". It is not a bug in the page — the visitor
 * holds HTML from before the deploy — and a reload hands them the current one.
 *
 * Two places notice it, and they share this one policy:
 *
 *   - Vite's `vite:preloadError`, fired on `window` for EVERY failed dynamic
 *     import and chunk preload of the build, whoever made the call
 *     (installStaleBundleReload, installed from main.tsx);
 *   - the error boundary, which sees the lazy routes' failure
 *     (components/ErrorBoundary.tsx).
 *
 * The listener does NOT call `event.preventDefault()`, although that is the
 * usual recipe. Prevented, Vite does not throw: the import resolves to
 * `undefined`, and React.lazy then reads `.default` off it — the boundary
 * catches "Cannot read properties of undefined" and reports a crash of its
 * own, with the generic card instead of the stale-bundle one, until the page
 * has actually reloaded. Left alone, the original error goes on exactly as it
 * did before this listener existed (the boundary's explanation, the record
 * tagged `chunk`), and the reload is what is new for every failure the
 * boundary never saw: a stylesheet preload, an import called from an effect.
 *
 * Both places ask the same function, and the listener always asks first: Vite
 * dispatches the event before it throws. So the answer is "a reload is under
 * way", not "I just started one" — the boundary that asks second must hear
 * true too, or every first failure is logged as a crash while the page is
 * already reloading (2026-10-20: five such rows on prd, every chunk present).
 *
 * Browser-only on purpose: nothing here touches `window` until it is called,
 * so the module is safe in the SSR bundle (ErrorBoundary is in it), and the
 * reload happens from an event, never during a render, so it cannot disturb a
 * hydration.
 */

import { ADMIN_PATH } from './adminPath';

/**
 * One attempt per session, so an asset that is genuinely gone (a bad deploy, a
 * CDN hiccup) shows the card instead of reloading forever. No sessionStorage
 * means no loop protection, which means no automatic reload.
 */
export const CHUNK_RELOAD_KEY = 'ere:chunk-reload';

/**
 * Two surfaces load a lazy chunk while someone is mid-edit — the admin panel
 * (tab switch) and the partner portal — and there a silent reload would throw
 * away typed work to fix a cosmetic problem. They get the explanation and a
 * button instead, so the choice stays theirs.
 */
function holdsUnsavedWork(pathname: string): boolean {
  return pathname.startsWith(ADMIN_PATH) || /^\/(nl|en)\/partner-portal/.test(pathname);
}

/** Set when this page load started its reload; a reload starts a fresh module. */
let reloadUnderWay = false;

/**
 * Reloads the page once per session unless it holds unsaved work. True when
 * a reload is under way — started by this call or an earlier one on this page
 * (the `vite:preloadError` listener runs before the boundary or the
 * unhandled-rejection hook gets to ask). reportClientError posts via sendBeacon, which the
 * browser delivers across the navigation — a report made just before still
 * reaches the admin error log.
 */
export function reloadOnceForStaleBundle(): boolean {
  if (reloadUnderWay) return true;
  try {
    if (holdsUnsavedWork(window.location?.pathname ?? '')) return false;
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, new Date().toISOString());
    window.location.reload();
    reloadUnderWay = true;
    return true;
  } catch {
    /* private mode / storage disabled — stay on the card rather than risk a loop */
    return false;
  }
}

/**
 * True for the browsers' ways of saying a lazy chunk did not load. Also for an
 * error that wraps one as its `cause`: a chunk that fails while a baked page
 * hydrates comes back from React as #520 ("an error while hydrating"), with
 * the import's own error as the cause — one incident, which the boundary
 * already reports as `chunk`.
 */
export function isChunkLoadError(error: unknown, depth = 0): boolean {
  if (!(error instanceof Error) || depth > 2) return false;
  if (
    /dynamically imported module|Loading chunk|module script failed|Importing a module/i.test(
      error.message ?? ''
    )
  ) {
    return true;
  }
  return isChunkLoadError((error as Error & { cause?: unknown }).cause, depth + 1);
}

/** Tests only: forget that this page started a reload. */
export function _resetStaleBundleForTests(): void {
  reloadUnderWay = false;
}

/** Reload on Vite's `vite:preloadError`, by the policy above. Called once, from the client entry. */
export function installStaleBundleReload(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('vite:preloadError', () => {
    reloadOnceForStaleBundle();
  });
}
